import React from 'react'
import { Avatar, Box, Tooltip } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { AGE, C, TONE, tnum } from './tokens'
import { CHANNELS, ago, fullStamp, initials, smartTime, span, statusMeta } from './model'

export function StatusBadge({ status, sx }) {
  const meta = statusMeta(status)
  const t = TONE[meta.tone] || TONE.muted
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', height: 24, px: 1, borderRadius: 999,
        fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', color: t.fg, bgcolor: t.bg, ...sx,
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: t.dot, flexShrink: 0 }} />
      {meta.label}
    </Box>
  )
}

const CHANNEL_SX = {
  UNASSIGNED: { border: `1px dashed ${C.input}`, color: C.muted, bgcolor: 'transparent' },
  RUSHMYFOOD: { border: '1px solid rgba(46,125,50,0.25)', color: C.brandGreen, bgcolor: 'rgba(46,125,50,0.08)' },
  FIRST_DELIVERY: { border: `1px solid ${C.ink}`, color: '#fff', bgcolor: C.ink },
  STAFF: { border: '1px solid rgba(3,105,161,0.2)', color: C.sky, bgcolor: C.skyBg },
  // The Orders page's "RushMyFood (old)" chip colours.
  RUSHMYFOOD_OLD: { border: '1px solid rgba(190,24,93,0.25)', color: '#be185d', bgcolor: '#fdf2f8' },
}

export function ChannelBadge({ channel, sx }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', height: 20, px: '6px', borderRadius: '5px',
        fontSize: 11, fontWeight: 500, lineHeight: 1, whiteSpace: 'nowrap', ...CHANNEL_SX[channel], ...sx,
      }}
    >
      {CHANNELS[channel]}
    </Box>
  )
}

const AGE_WORDS = {
  fresh: 'Under 15 minutes old',
  warn: '15–45 minutes old',
  late: 'Over 45 minutes old',
  stale: 'Open for more than a day — likely never closed out',
}

/** Time since the order was placed, colored by how worried to be about it. */
export function AgeBadge({ view, level, now }) {
  const a = AGE[level] || AGE.done
  let label
  if (level === 'done') {
    label = <Box component="span" sx={{ fontSize: 13, color: C.muted, ...tnum, whiteSpace: 'nowrap' }}>{view.placedAt ? ago(view.placedAt, now) : '—'}</Box>
  } else if (level === 'scheduled') {
    label = (
      <Pill a={a}>
        {view.scheduledFor ? `for ${smartTime(view.scheduledFor, now, view.restaurant?.timezone)}` : 'Scheduled'}
      </Pill>
    )
  } else {
    label = (
      <Pill a={a}>
        {span(now - view.placedAt)}
        {level === 'stale' && (
          <Box component="span" sx={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', opacity: 0.8 }}>
            stale
          </Box>
        )}
      </Pill>
    )
  }
  return (
    <Tooltip
      placement="right"
      arrow
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <span style={{ fontWeight: 600 }}>Placed {view.placedAt ? fullStamp(view.placedAt, view.restaurant?.timezone) : '—'}</span>
          {level === 'scheduled' && view.scheduledFor && <span>Scheduled for {fullStamp(view.scheduledFor, view.restaurant?.timezone)}</span>}
          {AGE_WORDS[level] && <span style={{ opacity: 0.75 }}>{AGE_WORDS[level]}</span>}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'inline-flex', cursor: 'default' }}>{label}</Box>
    </Tooltip>
  )
}

function Pill({ a, children }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '6px', height: 24, px: '6px', borderRadius: '6px',
        fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', color: a.ink, bgcolor: a.bg, ...tnum,
      }}
    >
      <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: a.bar, flexShrink: 0 }} />
      {children}
    </Box>
  )
}

const hueOf = s => {
  let h = 0
  for (const ch of String(s || '')) h = (h * 31 + ch.charCodeAt(0)) % 360
  return h
}

/** The restaurant's own image, or a tinted monogram when there isn't one. */
export function RestaurantLogo({ restaurant, size = 24 }) {
  const hue = hueOf(restaurant.name)
  return (
    <Avatar
      variant="rounded"
      src={restaurant.image || undefined}
      alt=""
      sx={{
        width: size, height: size, borderRadius: '6px', fontSize: size * 0.4, fontWeight: 700,
        bgcolor: `hsl(${hue} 75% 93%)`, color: `hsl(${hue} 60% 32%)`,
        boxShadow: `inset 0 0 0 1px hsl(${hue} 50% 82%)`,
      }}
    >
      {initials(restaurant.name)}
    </Avatar>
  )
}

export function PersonAvatar({ name, photo, size = 24 }) {
  const hue = hueOf(name)
  return (
    <Avatar
      src={photo || undefined}
      alt=""
      sx={{ width: size, height: size, fontSize: size * 0.4, fontWeight: 600, bgcolor: `hsl(${hue} 60% 90%)`, color: `hsl(${hue} 55% 30%)` }}
    >
      {initials(name)}
    </Avatar>
  )
}

export function ZeroTotalFlag({ children }) {
  return (
    <Tooltip arrow title="Total is $0.00 — no payment was captured. Check it before dispatching.">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: AGE.warn.ink }}>
        <WarningAmberRoundedIcon sx={{ fontSize: 15 }} aria-label="Zero total" />
        {children}
      </Box>
    </Tooltip>
  )
}

